import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { DataGridPremium, GridColDef, useGridApiRef } from '@mui/x-data-grid-premium';
import { StackedTextCell } from '@retail/components';
import { DateSelectField, PricePerUnit } from '@retail/products/components';
import { formatPrice } from '@retail/products/utils';
import {
  calculatePercentChange,
  calculateSalesCoverageFromCostAndSalesPrice,
  getDataGridLocalization,
  getLLDate,
  MgPrisLocale,
} from '@retail/utils';
import { DeleteIcon } from '@shared/custom-icons';
import { dataGridCellNoFocusOutlineClass } from '@shared/styles';
import clsx from 'clsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppTFunction } from '@retail/app/i18n';
import { SalesPriceTextField } from './SalesPriceTextField';
import { Controller, useFormContext } from 'react-hook-form';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { BulkEditPricesSchema, PriceItemSchema } from './bulkEditPricesSchema';
import { InfoOutline } from '@mui/icons-material';

interface Props {
  items: AssortmentPrices[];
  includeVat: boolean;
  handleRemoveItem: (itemId: number) => void;
}

type AssortmentItemColumn = GridColDef<AssortmentPrices>;

const baseColumnProps: Partial<AssortmentItemColumn> = {
  width: 220,
  sortable: true,
  align: 'left',
  headerAlign: 'left',
  headerName: '',
};

export function BulkEditSalesPriceTable({ items, includeVat, handleRemoveItem }: Props) {
  const t = useAppTFunction();
  const apiRef = useGridApiRef();
  const {
    i18n: { language },
  } = useTranslation();

  const { control, watch } = useFormContext<BulkEditPricesSchema<PriceItemSchema>>();

  const columns: AssortmentItemColumn[] = useMemo(
    () => [
      {
        ...baseColumnProps,
        field: 'itemText',
        headerName: t('myAssortment.columns.itemText'),
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={row.primaryText}
            bottomLine={row.secondaryText}
          />
        ),
        valueGetter: (_, row) => row.primaryText,
      },
      {
        ...baseColumnProps,
        field: 'supplier',
        headerName: t('myAssortment.columns.supplier'),
        renderCell: ({ row }) => (
          <StackedTextCell
            nullableBottomLine
            topLine={row.supplier.name}
            bottomLine={row.supplier.id}
          />
        ),
        valueGetter: (_, row) => row.supplier.id,
      },
      {
        ...baseColumnProps,
        field: 'currentSalesPrice',
        headerName: t('myAssortment.columns.currentSalesPrice'),
        width: 170,
        renderCell: ({ row }) => {
          const currentPriceValidFrom = getLLDate(row.salesPriceDate);
          const currentSalesPrice = includeVat
            ? row.pricesIncVat.salesPrice
            : row.pricesExVat.salesPrice;

          return (
            <StackedTextCell
              topLine={
                <PricePerUnit
                  price={currentSalesPrice ? formatPrice(currentSalesPrice, language) : '-'}
                  unit={row.primaryUnit}
                />
              }
              bottomLine={currentPriceValidFrom}
              nullableBottomLine
            />
          );
        },
        valueGetter: (_, row) => row.pricesExVat.salesPrice,
      },
      {
        ...baseColumnProps,
        field: 'fromDate',
        headerName: t('myAssortment.columns.priorityValidFrom'),
        renderHeader: () => (
          <Stack flexDirection="row" alignItems="center" gap={4.5}>
            <Typography variant="subtitle1">{t('myAssortment.columns.validFrom')}</Typography>
          </Stack>
        ),
        headerAlign: 'center',
        width: 210,
        align: 'center',
        type: 'singleSelect',
        renderCell: ({ row }) => {
          const currentCostPrice = row.pricesExVat.costPrice;
          return (
            <Controller
              name={`items.${row.mgSupplierItemId}.fromDate`}
              control={control}
              render={({ field }) => (
                <DateSelectField
                  disabled={!currentCostPrice}
                  value={currentCostPrice ? field.value : undefined}
                  minDate={new Date()}
                  setNewDate={(newDate: Date | null) => {
                    field.onChange(newDate);
                  }}
                />
              )}
            />
          );
        },
        valueGetter: (_, row) => row.priority.validFrom,
      },
      {
        ...baseColumnProps,
        field: 'salesPrice',
        headerName: t('myAssortment.columns.newSalesPrice'),
        width: 130,
        renderCell: (params) => {
          const currentCostPrice = params.row.pricesExVat.costPrice;

          const handleKeyDown = (event: React.KeyboardEvent) => {
            if (event.key === 'Enter') {
              event.stopPropagation();
              event.preventDefault();

              const currentRowIndex = apiRef?.current?.getRowIndexRelativeToVisibleRows(params.id);
              const nextRowId =
                currentRowIndex !== undefined
                  ? apiRef?.current?.getRowIdFromRowIndex(currentRowIndex + 1)
                  : undefined;

              if (nextRowId) {
                setTimeout(() => {
                  apiRef?.current?.setCellFocus(nextRowId, params.field);

                  const selector = `[data-id="${nextRowId}"] [data-field="${params.field}"] input`;
                  const nextInput = document.querySelector(selector) as HTMLInputElement;

                  if (nextInput) {
                    nextInput.focus();
                  }
                }, 10);
              }
            }
          };

          return (
            <Stack flexDirection="row" alignItems="center" gap={0.5}>
              <SalesPriceTextField
                fieldName={params.field}
                mgSupplierItemId={params.row.mgSupplierItemId}
                disabled={!currentCostPrice}
                apiRef={apiRef}
                handleKeyDown={handleKeyDown}
              />
              {!currentCostPrice && (
                <Tooltip
                  title={
                    <Stack gap={1}>
                      <Typography variant="subtitle2">
                        {t('myAssortment.productCard.salesPrice.error.noCostPrice')}
                      </Typography>
                      <Typography variant="body2">
                        {t('myAssortment.status.noMarkupWithoutCostPrice')}
                      </Typography>
                    </Stack>
                  }
                >
                  <InfoOutline fontSize="small" />
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
      {
        ...baseColumnProps,
        field: 'percentageChange',
        headerName: t('myAssortment.columns.percentageChange'),
        headerAlign: 'center',
        align: 'center',
        width: 130,
        renderCell: ({ row }) => {
          const currentSalesPrice = includeVat
            ? row.pricesIncVat.salesPrice
            : row.pricesExVat.salesPrice;
          const newPrice = watch(`items.${row.mgSupplierItemId}.salesPrice`);

          const percentChange =
            currentSalesPrice && newPrice && !!Number(newPrice)
              ? calculatePercentChange(currentSalesPrice, Number(newPrice))
              : undefined;

          return percentChange ? `${formatPrice(percentChange, language)}%` : '';
        },
      },
      {
        ...baseColumnProps,
        field: 'salesCoverage',
        headerName: t('myAssortment.columns.newSalesCoverage'),
        headerAlign: 'center',
        align: 'center',
        width: 130,
        renderCell: ({ row }) => {
          const currentCostPrice = includeVat
            ? row.pricesIncVat.costPrice
            : row.pricesExVat.costPrice;
          const newPrice = watch(`items.${row.mgSupplierItemId}.salesPrice`);

          if (!newPrice || !Number(newPrice) || !currentCostPrice) return '';

          const calculatedSalesCoverage = calculateSalesCoverageFromCostAndSalesPrice(
            currentCostPrice,
            newPrice
          );
          return `${formatPrice(calculatedSalesCoverage, language)}%`;
        },
      },

      {
        field: 'actions',
        headerName: '',
        minWidth: 60,
        maxWidth: 60,
        sortable: false,
        disableColumnMenu: true,
        renderCell: ({ row }) => (
          <IconButton onClick={() => handleRemoveItem(row.mgSupplierItemId)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        ),
      },
    ],
    [apiRef, control, handleRemoveItem, includeVat, language, t, watch]
  );

  return (
    <Box sx={{ flexGrow: 1, position: 'relative', height: 500 }}>
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <DataGridPremium<AssortmentPrices>
          getRowId={(row) => row.mgSupplierItemId}
          columns={columns}
          rows={items}
          apiRef={apiRef}
          rowHeight={70}
          getCellClassName={() => clsx(dataGridCellNoFocusOutlineClass)}
          disableRowSelectionOnClick
          disableColumnResize
          disableColumnReorder
          disableColumnMenu
          hideFooter
          localeText={getDataGridLocalization(language as MgPrisLocale)}
          initialState={{ pinnedColumns: { right: ['actions'] } }}
        />
      </Box>
    </Box>
  );
}
